import compose from 'recompose/compose';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import get from 'lodash/get';
import LoadingBar from 'react-redux-loading-bar';

import {
  getFirstLogin,
  getFrontendFlags,
  getIsRentalBarber,
} from '../auth/selectors';
import withLoansSidebarButtons from '../loans/withLoansSidebarButtons';
import { fetchBarberActiveRegister } from '../register/cashRegister/actions';
import withSidebarStateAndContext from '../common/hoc/withSidebarStateAndContext';
import FirstLoginWelcome from '../help/FirstLoginWelcome';
import Main from '../main/main';
import Sidebar from '../sidebar/sidebar';
import { barberUpdated } from '../main/barbers/actions';
import { getAppointment } from '../main/appointments/actions';
import { GLYPHS } from '../common/icon';
import {
  canUserViewServices,
  canUserViewCustomers,
  canUserViewReports,
  canUserViewRegister,
  canUseEngage,
} from '../common/helpers/users';
import withCollapsed from '../sidebar/withCollapsed';
import HeaderShopsContainer from './shopsContainer';
import {
  subscribe,
  unsubscribe,
  setBarberFilter,
  unsetBarberFilter,
} from '../common/pubnub';
import { getBarberBankAccount, getBarberMerchantInfo } from '../main/settings/actions';
import { fetchTimerBarber } from '../main/timeTracker/actions';
import { containerApp } from '../../main';
import { getJobsStatus } from '../queue/actions';
import { fetchBarberDiscounts } from '../main/settings/discounts/actions';
import { fetchLoyalty as fetchBarberLoyalty } from '../main/settings/loyalty/actions';
import withTimeTrackingSync from '../main/timeTracker/hoc/withTimeTrackingSync';
import HandoffListener from '../handoff/handoffListener';
import { barberFetchWaitingListCustomers } from '../main/waitingList/waitingListCustomers/actions';
import RegisterButton from '../common/RegisterButton';
import FixedButtonsContainer from '../common/FixedButtonsContainer';
import { fetchBarberCampaign } from '../engagement/actions';
import { ConciergeChat, getConciergeManager } from '../../modules/shared/concierge';
import { withFeatureFlags } from '../../modules/infra/feature-flags';
import { BarberViewPopups } from '../../modules/shared/app-start-popups/barber-view-popups';
import { withBarberType } from '../../modules/barber/barber-type/with-barber-type';
import { canUserSeeEngage } from '../../modules/shared/engage/engage-access-utils';
import config from '../../config';

class BarberApp extends Component {
  constructor(props) {
    super(props);
    this.state = { marginClass: 'collapse' };
  }

  componentDidMount() {
    this.initNotificationListener();
    this.props.getBarberMerchantInfo(this.props.barberUser.id);
    this.props.fetchTimerBarber(this.props.barberUser.id);
    this.props.fetchBarberDiscounts(this.props.barberUser.id);
    this.props.fetchBarberLoyalty(this.props.barberUser.id, 'barber');
    
    if (this.props.isRentalBarber) {
      this.props.fetchBarberActiveRegister(this.props.barberUser.id);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.shop !== this.props.shop) {
      this.initNotificationListener();
      unsubscribe(`shop_${prevProps.shop}`);
      this.props.getBarberMerchantInfo(this.props.barberUser.id);
    }
  }

  componentWillUnmount() {
    unsetBarberFilter();
  }

  initNotificationListener = () => {
    setBarberFilter(this.props.barberUser.id);
    subscribe({
      channel: `shop_${this.props.shop}`,
      message: ({ message }) => {
        this.barbers(message);
        this.appointments(message);
        this.queueJobs(message);
        this.timeTrack(message);
        this.waitingList(message);
        this.campaign(message);
      },
    });
  };

  queueJobs = (args) => {
    const finishedJob = get(args, 'queues.finished');

    if (finishedJob) {
      this.props.getJobsStatus(
        finishedJob.queueName,
        finishedJob.jobName,
        {
          shopId: this.props.shop,
          barberId: this.props.barberUser.id,
        }
      );
    }
  };

  appointments = (args) => {
    const booked = get(args, 'appointments.booked');
    const updated = get(args, 'appointments.updated');
    const canceled = get(args, 'appointments.canceled');

    if (booked) {
      this.props.getAppointment(booked, 'booked');
    }

    if (updated) {
      this.props.getAppointment(updated, 'updated');
    }

    if (canceled) {
      this.props.getAppointment(canceled, 'canceled');
    }
  };

  barbers = (args) => {
    const updated = get(args, 'barbers.updated');
    if (updated && this.props.barberUser.id === updated) {
      this.props.barberUpdated(updated);
    }
  };

  timeTrack = (args) => {
    const update = get(args, 'timeTrack.toggled');
    if (update) {
      console.log('Pubnub: time track toggled [barber]', update);
      this.props.syncTimeTrack(update);
    }
  };

  waitingList = (args) => {
    const waitingListDate = get(args, 'waitingList.day');
    if (waitingListDate) {
      this.props.barberFetchWaitingListCustomers(this.props.barberUser.id, waitingListDate);
    }
  };

  campaign = (args) => {
    const campaign = get(args, 'campaign.updated');
    if (campaign) {
      this.props.fetchBarberCampaign(this.props.barberUser.id, campaign);
    }
  };

  // eslint-disable-next-line class-methods-use-this
  openConcierge = async () => {
    const chatManager = await getConciergeManager();
    chatManager.open();
  };

  render() {
    const { permissions } = this.props;
    const shouldShowConcierge = !containerApp.isActive() && !this.props.isUserImpersonated;

    const data = [{
      id: 'appointments',
      text: 'Appointments',
      iconName: GLYPHS.appointment,
      url: '/barber/appointments',
      show: true,
    }];

    data.push({
      id: 'clients',
      text: 'Clients',
      iconName: GLYPHS.customers,
      url: '/barber/customers',
      show: canUserViewCustomers(permissions),
    });

    data.push({
      id: 'profile',
      text: 'Profile',
      iconName: GLYPHS.barbers,
      url: '/barber/profile',
      show: true,
    });

    data.push({
      id: 'services',
      text: 'Services',
      iconName: GLYPHS.services,
      url: '/barber/services',
      show: canUserViewServices(permissions),
    });

    data.push({
      id: 'reports',
      text: 'Reports',
      iconName: GLYPHS.reports,
      url: '/barber/reports',
      show: canUserViewReports(permissions),
    });

    data.push({
      id: 'products',
      text: 'Products',
      iconName: GLYPHS.packages,
      url: '/barber/products',
      show: true,
    });

    data.push({
      id: 'engage',
      text: 'Engage',
      iconName: GLYPHS.engagement,
      url: '/barber/engagement',
      show: this.props.canUseEngage,
      label: {
        when: !this.props.frontendFlags.engagementOnboarded,
        text: 'New',
      },
    });

    data.push({
      id: 'help',
      text: 'Help Center',
      iconName: GLYPHS.help,
      href: config.salesForceHelp.url,
      show: true,
      explainer: {
        when: this.props.firstLogin,
        component: FirstLoginWelcome,
      },
    });

    data.push({
      id: 'settings',
      text: 'Settings',
      iconName: GLYPHS.settings,
      url: '/barber/settings/change-password',
      show: true,
    });

    data.push(this.props.loansButton);

    data.push({
      id: 'concierge',
      text: 'Chat with Support',
      iconName: GLYPHS.concierge,
      onClick: this.openConcierge,
      show: shouldShowConcierge,
    });

    data.push({
      id: 'referrals',
      text: 'Refer others, get rewarded',
      iconName: GLYPHS.referral,
      href: 'https://getsqr.co/referral',
      linkAttrs: { target: '_blank' },
      show: true,
    });

    const className = classnames({
      collapse: this.props.collapsed,
      expand: !this.props.collapsed,
      container: containerApp.isActive(),
    });

    return (
      <div>
        <LoadingBar className="c-loading-bar" />
        <Sidebar
          navData={data}
          collapsed={this.props.collapsed}
          setCollapsed={this.props.setCollapsed}
          display-if={!containerApp.isActive()}
        >
          <HeaderShopsContainer
            collapsed={this.props.collapsed && !this.props.sidebarActive}
          />
        </Sidebar>
        <ConciergeChat display-if={shouldShowConcierge} />
        <Main marginClass={className}>
          {this.props.children}
        </Main>
        <HandoffListener />
        <FixedButtonsContainer>
          <RegisterButton display-if={canUserViewRegister(permissions)} url="/barber/register" />
        </FixedButtonsContainer>
        <BarberViewPopups
          barberId={this.props.barberUser.id}
          getBarberBankAccount={this.props.getBarberBankAccount}
        />
      </div>
    );
  }
}

BarberApp.propTypes = {
  children: PropTypes.node.isRequired,
  permissions: PropTypes.object.isRequired,
  collapsed: PropTypes.bool.isRequired,
  setCollapsed: PropTypes.func.isRequired,
  barberUpdated: PropTypes.func.isRequired,
  barberUser: PropTypes.object.isRequired,
  shop: PropTypes.string.isRequired,
  getAppointment: PropTypes.func.isRequired,
  getBarberMerchantInfo: PropTypes.func.isRequired,
  fetchTimerBarber: PropTypes.func.isRequired,
  fetchBarberActiveRegister: PropTypes.func.isRequired,
  isRentalBarber: PropTypes.bool.isRequired,
  firstLogin: PropTypes.bool.isRequired,
  sidebarActive: PropTypes.bool.isRequired,
  frontendFlags: PropTypes.shape({
    engagementOnboarded: PropTypes.bool,
  }).isRequired,
  getJobsStatus: PropTypes.func.isRequired,
  fetchBarberDiscounts: PropTypes.func.isRequired,
  fetchBarberLoyalty: PropTypes.func.isRequired,
  syncTimeTrack: PropTypes.func.isRequired,
  barberFetchWaitingListCustomers: PropTypes.func.isRequired,
  loansButton: PropTypes.object.isRequired,
  getBarberBankAccount: PropTypes.func.isRequired,
  fetchBarberCampaign: PropTypes.func.isRequired,
  isUserImpersonated: PropTypes.bool.isRequired,
  canUseEngage: PropTypes.bool.isRequired,
};

const mapStateToProps = (state, ownProps) => ({
  permissions: state.auth.permissions,
  shop: state.auth.shop,
  barberUser: state.auth.barberUser,
  firstLogin: getFirstLogin(state),
  frontendFlags: getFrontendFlags(state),
  isRentalBarber: getIsRentalBarber(state),
  isUserImpersonated: state.auth.isUserImpersonated,
  canUseEngage: canUserSeeEngage({
    permissions: state.auth.permissions,
    flags: ownProps.featureFlags,
    engageEnabledForBrand: canUseEngage(state),
    isBarberView: true,
    barberType: ownProps.barberType,
  }),
});

const mapDispatchToProps = {
  barberUpdated,
  getAppointment,
  getBarberMerchantInfo,
  fetchTimerBarber,
  getJobsStatus,
  fetchBarberActiveRegister,
  fetchBarberDiscounts,
  fetchBarberLoyalty,
  barberFetchWaitingListCustomers,
  getBarberBankAccount,
  fetchBarberCampaign,
};

export default compose(
  withFeatureFlags,
  withBarberType,
  connect(mapStateToProps, mapDispatchToProps),
  withLoansSidebarButtons,
  withSidebarStateAndContext,
  withCollapsed,
  withTimeTrackingSync,
)(BarberApp);
